import { useCallback, useEffect, useRef, useState } from 'react';
import { empresaCarregar } from '../Utils/Services';
import { applyEmpresaTheme, resetEmpresaTheme } from '../Utils/empresaTheme';
import { DeliveryTerms, saveDeliveryTerms } from '../Utils/deliveryAddressStorage';
import { clearStoreOrderTerms, normalizeEmpresaId, readStoreAccess, resolveStoreEntry, saveStoreAccess, StoreAccess } from '../Utils/storeAccess';

class StoreAccessError extends Error {}

type StoreSelection = {
  lookup: string;
  accessMode: StoreAccess['accessMode'];
  deliveryTerms?: DeliveryTerms;
};

export function useStoreAccessState(setDadosEmpresa: (company: any) => void, setListaItemProdutos: (items: any[]) => void) {
  const [{ entry, previous }] = useState(() => ({ entry: resolveStoreEntry(), previous: readStoreAccess() }));
  const [storeAccess, setAccess] = useState<StoreAccess>({ accessMode: entry.accessMode, empresaid: entry.empresaid });
  const accessRef = useRef(storeAccess);
  // Um slug ainda não resolvido não é uma troca de empresa. Compare com a última loja confirmada.
  const loadedStoreId = useRef(previous.empresaid);
  const [storeLoading, setStoreLoading] = useState(Boolean(entry.lookup));
  const [storeError, setStoreError] = useState(entry.error);
  const request = useRef<{ sequence: number; controller?: AbortController }>({ sequence: 0 });
  const retrySelection = useRef<StoreSelection | null>(null);

  const updateAccess = useCallback((access: StoreAccess) => {
    accessRef.current = access;
    saveStoreAccess(access);
    setAccess(access);
  }, []);

  const loadStore = useCallback(async (selection: StoreSelection): Promise<boolean> => {
    const { lookup, accessMode, deliveryTerms } = selection;
    const sequence = ++request.current.sequence;
    request.current.controller?.abort();
    const controller = new AbortController();
    request.current.controller = controller;
    retrySelection.current = selection;
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    setStoreLoading(true);
    setStoreError('');
    try {
      const response = await fetch(empresaCarregar.url.replace('{id}', encodeURIComponent(lookup)), {
        method: empresaCarregar.method, credentials: 'include', signal: controller.signal,
        headers: Object.fromEntries(['token', 'versao', 'perfil', 'marca'].map(key => [key, localStorage.getItem(key) || '']))
      });
      const result = await response.json();
      if (sequence !== request.current.sequence) return false;
      if (controller.signal.aborted) throw new StoreAccessError('Não foi possível carregar esta loja. Tente novamente.');
      const company = result?.Item;
      if (!response.ok || !normalizeEmpresaId(company?.pk)) {
        throw new StoreAccessError(response.status === 400 || response.status === 404 || response.ok
          ? 'Não foi possível encontrar esta loja.' : 'Não foi possível carregar esta loja. Tente novamente.');
      }
      if (lookup.startsWith('empresa#') && company.pk !== lookup) throw new StoreAccessError('A loja retornada não corresponde ao link informado.');
      const changedStore = loadedStoreId.current !== company.pk;
      if (changedStore) {
        clearStoreOrderTerms();
        if (accessMode === 'store') {
          ['enderecoEntrega', 'latitudeEntrega', 'longitudeEntrega', 'complementoEntrega', 'bairroEntrega']
            .forEach(key => localStorage.removeItem(key));
        }
      }
      // A vitrine fornece uma prévia. Falhas de entrega não podem impedir acesso ao catálogo;
      // o carrinho consulta e valida as condições atuais antes de avançar.
      if (deliveryTerms) saveDeliveryTerms(deliveryTerms.taxa, deliveryTerms.tempo);
      loadedStoreId.current = company.pk;
      localStorage.setItem('empresanome', company.empresaNome || '');
      if (entry.slug) localStorage.setItem('slug', entry.slug);
      else localStorage.removeItem('slug');
      if (company.estrangeiro) localStorage.setItem('estrangeiro', 'true');
      else localStorage.removeItem('estrangeiro');
      document.title = company.empresaNome || 'Pedido';
      applyEmpresaTheme(company);
      updateAccess({ accessMode, empresaid: company.pk });
      if (changedStore) setListaItemProdutos([]);
      setDadosEmpresa(company);
      return true;
    } catch (error) {
      if (sequence === request.current.sequence) {
        setStoreError(error instanceof StoreAccessError ? error.message : 'Não foi possível carregar esta loja. Tente novamente.');
      }
      return false;
    } finally {
      window.clearTimeout(timeout);
      if (sequence === request.current.sequence) setStoreLoading(false);
    }
  }, [entry.slug, setDadosEmpresa, setListaItemProdutos, updateAccess]);

  useEffect(() => {
    const activeRequest = request.current;
    if (entry.lookup) loadStore({ lookup: entry.lookup, accessMode: entry.accessMode });
    else if (!entry.error) saveStoreAccess({ accessMode: entry.accessMode, empresaid: entry.empresaid });
    return () => { ++activeRequest.sequence; activeRequest.controller?.abort(); };
  }, [entry, loadStore]);

  const clearActiveStore = useCallback(() => {
    if (accessRef.current.accessMode !== 'marketplace') return;
    ++request.current.sequence;
    request.current.controller?.abort();
    retrySelection.current = null;
    loadedStoreId.current = null;
    updateAccess({ accessMode: 'marketplace', empresaid: null });
    setListaItemProdutos([]);
    setDadosEmpresa(false);
    ['empresanome', 'slug', 'estrangeiro'].forEach(key => localStorage.removeItem(key));
    clearStoreOrderTerms();
    setStoreLoading(false);
    setStoreError('');
    document.title = 'Xeguei';
    resetEmpresaTheme();
  }, [setDadosEmpresa, setListaItemProdutos, updateAccess]);

  const selectMarketplaceStore = useCallback((id: string, deliveryTerms?: DeliveryTerms) => {
    const empresaid = normalizeEmpresaId(id);
    return empresaid && accessRef.current.accessMode === 'marketplace'
      ? loadStore({ lookup: empresaid, accessMode: 'marketplace', deliveryTerms: deliveryTerms && { ...deliveryTerms } })
      : Promise.resolve(false);
  }, [loadStore]);
  const retryStore = useCallback(() => {
    return retrySelection.current ? loadStore(retrySelection.current) : Promise.resolve(false);
  }, [loadStore]);

  return { storeAccess, storeLoading, storeError, selectMarketplaceStore, clearActiveStore, retryStore };
}
